// IM-UX-166/170/171/172 (IM-ADR-020): administradores da plataforma (concessão, revogação, redefinição de fator,
// encerramento de sessões), sessões administrativas e trilha. Controles de escrita só aparecem para PLATFORM_ADMIN —
// e a regra vale no servidor de qualquer forma.
import { useState, type FormEvent } from 'react';
import { PLATFORM_AUDIT_LABEL, PLATFORM_ROLE_LABEL, platformCan, type PlatformAdminDto, type PlatformCandidateDto, type PlatformEnrollmentTicketDto, type PlatformRole, type PlatformSessionDto } from '@intelimove/contracts';
import { useAuth } from '../auth/AuthContext';
import { Modal } from '../components/Modal';
import { AsyncView, EmptyState } from '../components/States';
import { useToast } from '../components/Toast';
import { PageHead } from '../components/Widgets';
import { platformAdapter } from '../data/platform';
import { useResource } from '../hooks/useResource';
import { ReasonField, SensitiveForm, TicketReveal, apiMessage, day, dt, reloadTo } from './shared';

type Dialog = { kind: 'grant' } | { kind: 'revoke' | 'reset' | 'sessions'; admin: PlatformAdminDto };

const mfaTag = (a: PlatformAdminDto) => a.mfaConfirmed ? <span className="tag mint">Confirmado</span>
  : a.enrollmentPending ? <span className="tag amber">Cadastro pendente</span> : <span className="tag critical">Pendente</span>;

export function PlatformAdmins() {
  const { me } = useAuth();
  const toast = useToast();
  const role = me?.platform?.role;
  const [history, setHistory] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const { state, reload } = useResource((s) => platformAdapter.admins(history, s), [history], (d) => d.items.length === 0);
  const canWrite = platformCan(role, 'platform.admins.revoke');
  const close = (msg?: string) => { setDialog(null); if (msg) toast(msg); reload(); };
  return (
    <section aria-labelledby="page-title">
      <PageHead eyebrow="Administração da plataforma" title="Administradores" subtitle="Papéis da plataforma, segundo fator e acesso. Toda alteração exige motivo e código novo do aplicativo."
        actions={platformCan(role, 'platform.admins.grant') ? <button type="button" className="primary" onClick={() => setDialog({ kind: 'grant' })}>Conceder papel</button> : undefined} />
      <label className="pa-toggle"><input type="checkbox" checked={history} onChange={(e) => setHistory(e.target.checked)} /> Mostrar concessões revogadas</label>
      <AsyncView state={state} onRetry={reload} empty={<EmptyState title="Nenhum papel concedido" glyph="lock" />}>
        {(d) => (
          <article className="card">
            <div className="table-wrap"><table className="data-table stack">
              <caption className="sr-only">Papéis da plataforma</caption>
              <thead><tr><th scope="col">Pessoa</th><th scope="col">Papel</th><th scope="col">Situação</th><th scope="col">Segundo fator</th><th scope="col">Concedido</th><th scope="col">Último acesso</th>{canWrite && <th scope="col"><span className="sr-only">Ações</span></th>}</tr></thead>
              <tbody>{d.items.map((a) => {
                const self = a.userId === me?.user.id;
                return (
                  <tr key={a.grantId} className={a.revokedAt ? 'pa-revoked' : ''}>
                    <td data-label="Pessoa"><b>{a.name}</b>{self && <span className="tag gray">você</span>}<span className="cell-sub">{a.email} · {a.accountKind === 'PLATFORM' ? 'conta dedicada' : 'conta de cliente'}</span></td>
                    <td data-label="Papel">{PLATFORM_ROLE_LABEL[a.role]}<span className="cell-sub">{a.grantedVia === 'PLATAFORMA' ? `pela interface${a.grantedByName ? ` · ${a.grantedByName}` : ''}` : 'pelo operador'}</span></td>
                    <td data-label="Situação">{a.revokedAt ? <span className="tag gray">Revogado {day(a.revokedAt)}</span> : a.userStatus !== 'active' ? <span className="tag critical">Conta desativada</span> : <span className="tag mint">Ativo</span>}
                      {!a.revokedAt && a.openSessions > 0 && <span className="cell-sub">{a.openSessions} sessão(ões) aberta(s)</span>}</td>
                    <td data-label="Segundo fator">{a.revokedAt ? '—' : mfaTag(a)}{!a.revokedAt && a.mfaConfirmed && <span className="cell-sub">{a.recoveryRemaining} código(s) de recuperação</span>}</td>
                    <td data-label="Concedido">{day(a.grantedAt)}</td>
                    <td data-label="Último acesso">{dt(a.lastPlatformActivityAt ?? a.lastPlatformEntryAt)}</td>
                    {canWrite && <td data-label="Ações">{!a.revokedAt && (
                      <div className="pa-row-actions">
                        {!self && <button type="button" className="link-btn" onClick={() => setDialog({ kind: 'reset', admin: a })} aria-label={`Redefinir segundo fator de ${a.name}`}>Redefinir 2º fator</button>}
                        {!self && a.openSessions > 0 && <button type="button" className="link-btn" onClick={() => setDialog({ kind: 'sessions', admin: a })} aria-label={`Encerrar sessões de ${a.name}`}>Encerrar sessões</button>}
                        <button type="button" className="link-btn pa-danger-link" onClick={() => setDialog({ kind: 'revoke', admin: a })} aria-label={`Revogar papel de ${a.name}`}>Revogar</button>
                      </div>)}</td>}
                  </tr>);
              })}</tbody>
            </table></div>
          </article>
        )}
      </AsyncView>
      {dialog?.kind === 'grant' && <GrantDialog onClose={close} />}
      {dialog?.kind === 'revoke' && <RevokeDialog admin={dialog.admin} self={dialog.admin.userId === me?.user.id} onClose={close} />}
      {dialog?.kind === 'reset' && <ResetDialog admin={dialog.admin} onClose={close} />}
      {dialog?.kind === 'sessions' && <EndUserSessionsDialog admin={dialog.admin} onClose={close} />}
      {platformCan(role, 'platform.audit.read') && <PlatformAudit />}
    </section>
  );
}

// IM-UX-170: concessão a identidade existente (e-mail exato), sem autoelevação; código de cadastro entregue fora de banda.
function GrantDialog({ onClose }: { onClose: (msg?: string) => void }) {
  const [email, setEmail] = useState('');
  const [cand, setCand] = useState<PlatformCandidateDto | null>(null);
  const [role, setRole] = useState<PlatformRole>('PLATFORM_SUPPORT');
  const [ticket, setTicket] = useState<PlatformEnrollmentTicketDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const find = async (e: FormEvent) => {
    e.preventDefault();
    setError(null); setCand(null); setBusy(true);
    try { setCand(await platformAdapter.candidate(email.trim())); } catch (err) { setError(apiMessage(err, 'Não foi possível buscar agora.')); }
    setBusy(false);
  };
  if (ticket && cand) return (
    <Modal title="Papel concedido" onClose={() => onClose('Papel concedido.')}><TicketReveal ticket={ticket.ticket} expiresAt={ticket.expiresAt} who={cand.name} onDone={() => onClose('Papel concedido. Cadastro do segundo fator pendente.')} /></Modal>
  );
  return (
    <Modal title="Conceder papel da plataforma" onClose={() => onClose()}>
      <form className="form pa-form" onSubmit={find} noValidate>
        <div className="pa-inline-search"><div className="field"><label htmlFor="pa-cand">E-mail da conta existente</label><input id="pa-cand" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" /></div>
          <button className="secondary" type="submit" disabled={busy || !email.includes('@')}>Buscar</button></div>
        {error && !cand && <div className="form-error" role="alert">{error}</div>}
      </form>
      {cand && (
        <>
          <dl className="kv pa-cand"><dt>Pessoa</dt><dd><b>{cand.name}</b> · {cand.accountKind === 'PLATFORM' ? 'conta dedicada' : `conta de cliente (${cand.organizations} organização(ões))`}</dd>
            <dt>Segundo fator</dt><dd>{cand.mfaEnrolled ? 'Já cadastrado' : 'Será cadastrado pela pessoa com o código que você vai receber'}</dd></dl>
          {!cand.eligible ? <p className="form-error" role="alert">{cand.ineligibleReason}</p> : (
            <SensitiveForm idPrefix="pa-grant" submitLabel="Conceder papel" busyLabel="Concedendo…" onCancel={() => onClose()}
              onSubmit={async ({ reason, code }) => { const r = await platformAdapter.grant({ userId: cand.userId, role, reason, code, confirm: true }); if (r.ticket) setTicket(r.ticket); else onClose('Papel concedido.'); }}>
              <fieldset className="pa-radios"><legend>Papel</legend>
                {(['PLATFORM_SUPPORT', 'PLATFORM_ADMIN'] as const).map((r) => (
                  <label key={r}><input type="radio" name="pa-role" checked={role === r} onChange={() => setRole(r)} /> {PLATFORM_ROLE_LABEL[r]}<small>{r === 'PLATFORM_ADMIN' ? 'Altera organizações, papéis e sessões.' : 'Somente leitura de metadados.'}</small></label>))}
              </fieldset>
              <p className="cell-sub wide">O papel não dá acesso a dados de nenhuma organização cliente.</p>
            </SensitiveForm>
          )}
        </>
      )}
    </Modal>
  );
}

function RevokeDialog({ admin, self, onClose }: { admin: PlatformAdminDto; self: boolean; onClose: (msg?: string) => void }) {
  return (
    <Modal title="Revogar papel da plataforma" onClose={() => onClose()} role="alertdialog">
      <SensitiveForm idPrefix="pa-revoke" danger submitLabel="Revogar papel" busyLabel="Revogando…" onCancel={() => onClose()}
        onSubmit={async ({ reason, code }) => { const r = await platformAdapter.revoke(admin.grantId, reason, code); if (r.selfRevoked) reloadTo('/'); else onClose('Papel revogado. Sessões administrativas encerradas.'); }}>
        <p>{admin.name} deixa de ser {PLATFORM_ROLE_LABEL[admin.role].toLowerCase()}; sessões administrativas, cadastros pendentes e códigos de recuperação dessa pessoa deixam de valer agora.{self ? ' Como é o seu próprio papel, você sairá da administração.' : ''} Vínculos com organizações não mudam.</p>
      </SensitiveForm>
    </Modal>
  );
}

// IM-UX-171: redefinição do segundo fator de outra pessoa (aparelho perdido).
function ResetDialog({ admin, onClose }: { admin: PlatformAdminDto; onClose: (msg?: string) => void }) {
  const [ticket, setTicket] = useState<PlatformEnrollmentTicketDto | null>(null);
  if (ticket) return <Modal title="Segundo fator redefinido" onClose={() => onClose('Segundo fator redefinido.')}><TicketReveal ticket={ticket.ticket} expiresAt={ticket.expiresAt} who={admin.name} onDone={() => onClose('Segundo fator redefinido.')} /></Modal>;
  return (
    <Modal title="Redefinir segundo fator" onClose={() => onClose()} role="alertdialog">
      <SensitiveForm idPrefix="pa-reset" danger submitLabel="Redefinir segundo fator" busyLabel="Redefinindo…" onCancel={() => onClose()}
        onSubmit={async ({ reason, code }) => setTicket((await platformAdapter.resetMfa(admin.userId, reason, code)).ticket)}>
        <p>O aplicativo atual e os códigos de recuperação de {admin.name} deixam de valer, as sessões administrativas dessa pessoa são encerradas e você recebe um código de cadastro para entregar a ela. O papel não muda.</p>
      </SensitiveForm>
    </Modal>
  );
}

function EndUserSessionsDialog({ admin, onClose }: { admin: PlatformAdminDto; onClose: (msg?: string) => void }) {
  return <EndSessionDialog title={`Encerrar sessões de ${admin.name}`} text={`Todas as ${admin.openSessions} sessão(ões) administrativa(s) dessa pessoa serão encerradas. O papel continua; ela pode entrar de novo com o segundo fator.`}
    run={(reason) => platformAdapter.revokeUserSessions(admin.userId, reason).then((r) => `${r.sessionsRevoked} sessão(ões) encerrada(s).`)} onClose={onClose} />;
}

function EndSessionDialog({ title, text, run, onClose }: { title: string; text: string; run: (reason: string) => Promise<string>; onClose: (msg?: string) => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (reason.trim().length < 5) { setError('Explique o motivo (mínimo de 5 caracteres).'); return; }
    setBusy(true); setError(null);
    try { onClose(await run(reason.trim())); } catch (err) { setError(apiMessage(err, 'Não foi possível encerrar agora.')); setBusy(false); }
  };
  return (
    <Modal title={title} onClose={() => onClose()} role="alertdialog">
      <form className="form pa-form" onSubmit={submit} noValidate>
        <p>{text}</p>
        {error && <div className="form-error" role="alert">{error}</div>}
        <ReasonField id="pa-end-reason" value={reason} onChange={setReason} />
        <div className="form-actions"><button type="button" className="secondary" onClick={() => onClose()}>Cancelar</button><button className="cta danger" type="submit" disabled={busy}>{busy ? 'Encerrando…' : 'Encerrar'}</button></div>
      </form>
    </Modal>
  );
}

// IM-UX-172: sessões administrativas ativas.
const browser = (ua: string | null) => !ua ? '—' : /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : ua.slice(0, 40);
export function PlatformSessions() {
  const { me } = useAuth();
  const [target, setTarget] = useState<PlatformSessionDto | null>(null);
  const toast = useToast();
  const { state, reload } = useResource((s) => platformAdapter.sessions(s), [], (d) => d.items.length === 0);
  const canRevoke = platformCan(me?.platform?.role, 'platform.sessions.revoke');
  return (
    <section aria-labelledby="page-title">
      <PageHead eyebrow="Administração da plataforma" title="Sessões administrativas" subtitle="Sessões abertas na administração. Tokens nunca são exibidos." actions={<button type="button" className="secondary" onClick={reload}>Atualizar</button>} />
      <AsyncView state={state} onRetry={reload} empty={<EmptyState title="Nenhuma sessão aberta" glyph="lock" />}>
        {(d) => (
          <article className="card">
            <div className="table-wrap"><table className="data-table stack">
              <caption className="sr-only">Sessões administrativas ativas</caption>
              <thead><tr><th scope="col">Pessoa</th><th scope="col">Início</th><th scope="col">Última atividade</th><th scope="col">Origem</th><th scope="col">Segundo fator</th>{canRevoke && <th scope="col"><span className="sr-only">Ações</span></th>}</tr></thead>
              <tbody>{d.items.map((s) => (
                <tr key={s.sessionId}>
                  <td data-label="Pessoa"><b>{s.name}</b>{s.current && <span className="tag mint">esta sessão</span>}<span className="cell-sub">{s.role ? PLATFORM_ROLE_LABEL[s.role] : 'sem papel vivo'}</span></td>
                  <td data-label="Início">{dt(s.createdAt)}</td>
                  <td data-label="Última atividade">{dt(s.lastSeenAt)}<span className="cell-sub">expira {dt(s.expiresAt)}</span></td>
                  <td data-label="Origem">{browser(s.userAgent)}<span className="cell-sub">{s.ip ?? '—'}</span></td>
                  <td data-label="Segundo fator">{s.mfaMethod === 'RECOVERY' ? <span className="tag amber">Código de recuperação</span> : 'Aplicativo'}</td>
                  {canRevoke && <td data-label="Ações">{!s.current && <button type="button" className="link-btn pa-danger-link" onClick={() => setTarget(s)} aria-label={`Encerrar sessão de ${s.name}`}>Encerrar</button>}</td>}
                </tr>))}</tbody>
            </table></div>
          </article>
        )}
      </AsyncView>
      {target && <EndSessionDialog title="Encerrar sessão administrativa" text={`A sessão de ${target.name} iniciada em ${dt(target.createdAt)} será encerrada agora.`}
        run={(reason) => platformAdapter.revokeSession(target.sessionId, reason).then(() => 'Sessão encerrada.')} onClose={(m) => { setTarget(null); if (m) toast(m); reload(); }} />}
    </section>
  );
}

function PlatformAudit() {
  const { state, reload } = useResource((s) => platformAdapter.audit(s), [], (d) => d.items.length === 0);
  return (
    <article className="card pa-audit">
      <div className="card-head"><div><h2>Trilha administrativa</h2><div className="card-sub">Entradas, recusas, concessões, revogações, suspensões e sessões (mais recentes primeiro)</div></div></div>
      <AsyncView state={state} onRetry={reload} empty={<p className="card-sub">Nenhum evento registrado.</p>}>
        {(d) => (
          <ul className="pa-events">{d.items.map((e) => {
            const m = e.metadata as { role?: string; reason?: string; slug?: string; method?: string };
            return (
              <li key={e.id}><time dateTime={e.createdAt}>{dt(e.createdAt)}</time>
                <span><b>{PLATFORM_AUDIT_LABEL[e.action] ?? e.action}</b>{e.actorName ? ` · ${e.actorName}` : ' · operador'}{m.role ? ` · ${PLATFORM_ROLE_LABEL[m.role as 'PLATFORM_ADMIN'] ?? m.role}` : ''}{m.slug ? ` · ${m.slug}` : ''}{m.method === 'RECOVERY' ? ' · código de recuperação' : ''}{m.reason ? ` · “${m.reason}”` : ''}</span>
              </li>);
          })}</ul>
        )}
      </AsyncView>
    </article>
  );
}
